import { getSurface } from "../components/AadhaarCard/surfaces";
import { getEffect } from "../components/AadhaarCard/effects";
import { cardRadiusPx } from "../components/AadhaarCard/AadhaarCard";

/**
 * Renders the "Realistic" scene to a canvas so a download looks like the
 * on-screen preview: the card sitting tilted on a surface, with the backdrop,
 * contact shadow, vignette, grain and glare all present.
 *
 * Why this is composited here rather than screenshotted from the DOM:
 * RealisticStage.css builds its look almost entirely out of `mix-blend-mode`
 * (six layers) and CSS `filter`. html2canvas 1.4.1 silently ignores both, so
 * pointing it at the preview markup yields a flat card with the noise layers
 * pasted on opaque - worse than no effect at all. A `foreignObject` rasteriser
 * would honour blend modes but cannot see the page's webfonts, and the card's
 * Indic text depends on ten remote Noto families. Canvas2D is the one path
 * that supports both: `globalCompositeOperation` implements
 * multiply/screen/soft-light natively, and the card itself is still rastered
 * by html2canvas - the proven path the plain export already uses - so card
 * typography is unchanged.
 *
 * Layer order mirrors RealisticStage.css top to bottom. When that file
 * changes this needs the matching change; the two are kept in step by name.
 */

/* RealisticStage.css states its padding, shadows, tile sizes and offsets in
   fixed pixels. Those are only "correct" at the size the preview is actually
   seen at - a card of PREVIEW_REF_W across (PREVIEW_MAX in GeneratorPage, the
   desktop state). The export card is far wider, so every one of those fixed
   values is multiplied by `k` below; scaling by the capture scale alone would
   leave the grain six times too fine and the shadow a hairline. */
const PREVIEW_REF_W = 380;
const PAD_X_PX = 22.4; // .rp-surface padding: 3em 1.4em at a 16px base
const PAD_Y_PX = 48;
const CARD_ASPECT = 1.5858; // CR80

/* The same noise tiles RealisticStage.css uses, kept identical so the export
   grain is the grain on screen. */
const NOISE = {
  surface: {
    px: 260,
    url:
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='260' height='260'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.4 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E",
  },
  grain1: {
    px: 170,
    url:
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='170' height='170'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.6 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23g)'/%3E%3C/svg%3E",
  },
  grain2: {
    px: 60,
    url:
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Cfilter id='f'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.8' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.5 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23f)'/%3E%3C/svg%3E",
  },
  grain3: {
    px: 24,
    url:
      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24'%3E%3Cfilter id='h'%3E%3CfeTurbulence type='turbulence' baseFrequency='0.9' numOctaves='1' stitchTiles='stitch'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.7 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23h)'/%3E%3C/svg%3E",
  },
};

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load a scene layer."));
    img.src = src;
  });
}

/** Canvas2D `filter` is unsupported on older Safari; the export should lose
    the colour grade rather than throw or come out blank. */
function supportsFilter(ctx) {
  return typeof ctx.filter === "string";
}

function setFilter(ctx, value) {
  if (supportsFilter(ctx)) ctx.filter = value;
}

function clearFilter(ctx) {
  if (supportsFilter(ctx)) ctx.filter = "none";
}

function num(vars, key, fallback) {
  const v = parseFloat(vars[key]);
  return Number.isFinite(v) ? v : fallback;
}

function roundRectPath(ctx, x, y, w, h, r) {
  const rad = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.lineTo(x + w - rad, y);
  ctx.arcTo(x + w, y, x + w, y + rad, rad);
  ctx.lineTo(x + w, y + h - rad);
  ctx.arcTo(x + w, y + h, x + w - rad, y + h, rad);
  ctx.lineTo(x + rad, y + h);
  ctx.arcTo(x, y + h, x, y + h - rad, rad);
  ctx.lineTo(x, y + rad);
  ctx.arcTo(x, y, x + rad, y, rad);
  ctx.closePath();
}

/** Tiles a noise bitmap over a region under a blend mode, the way each CSS
    noise layer sits over its own box. */
function blendNoise(ctx, img, region, mode, alpha, tileScale) {
  if (alpha <= 0) return;
  const pattern = ctx.createPattern(img, "repeat");
  if (!pattern) return;
  // The CSS gives each tile a fixed background-size, so the tile has to grow
  // with the card or the export grain reads as a much finer noise than the
  // preview's. setTransform is skipped where unsupported rather than failing
  // the export - slightly finer grain is a better outcome than no download.
  if (tileScale && typeof pattern.setTransform === "function" && typeof DOMMatrix === "function") {
    try {
      pattern.setTransform(new DOMMatrix().scale(tileScale));
    } catch (err) {
      /* leave the tile at its native size */
    }
  }
  ctx.save();
  ctx.globalCompositeOperation = mode;
  ctx.globalAlpha = Math.min(alpha, 1);
  ctx.fillStyle = pattern;
  ctx.fillRect(region.x, region.y, region.w, region.h);
  ctx.restore();
}

/** Draws the backdrop. A photographed surface gets the same
    object-fit:cover + scale(1.06) + depth-of-field blur as `.rp-bg-photo`. A
    CSS recipe is rendered through html2canvas, which does support gradients,
    and is left sharp: in RealisticStage.css the blur sits on `.rp-bg-photo`
    alone, so the gradient surfaces are not blurred on screen either. */
async function drawBackdrop(ctx, surface, { w, h, scale, blurPx, html2canvas }) {
  if (surface.image) {
    const img = await loadImage(surface.image);
    const cover = Math.max(w / img.width, h / img.height) * 1.06;
    const dw = img.width * cover;
    const dh = img.height * cover;
    ctx.save();
    setFilter(ctx, `blur(${blurPx}px)`);
    ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
    clearFilter(ctx);
    ctx.restore();
    return;
  }

  const holder = document.createElement("div");
  holder.style.cssText =
    `position:fixed;left:-20000px;top:0;width:${w / scale}px;height:${h / scale}px;` +
    `background:${surface.css};`;
  document.body.appendChild(holder);
  try {
    const bg = await html2canvas(holder, {
      scale,
      backgroundColor: null,
      useCORS: true,
      logging: false,
    });
    ctx.drawImage(bg, 0, 0, w, h);
  } finally {
    holder.remove();
  }
}

/** Approximates the `.rp-card-wrap.damage-*` overlays: fine scratches and
    dust. The CSS uses repeating gradients, which Canvas2D has no direct
    equivalent for, so these are drawn procedurally at comparable density and
    opacity rather than pixel-matched. */
function drawDamage(ctx, level, rect, seed) {
  const spec = {
    light: { scratches: 14, dust: 26, alpha: 0.02 },
    medium: { scratches: 30, dust: 60, alpha: 0.035 },
    heavy: { scratches: 58, dust: 120, alpha: 0.05 },
  }[level];
  if (!spec) return;

  let s = seed || 1;
  const rand = () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };

  ctx.save();
  ctx.lineWidth = Math.max(1, rect.w * 0.0009);
  for (let i = 0; i < spec.scratches; i += 1) {
    const x = rect.x + rand() * rect.w;
    const y = rect.y + rand() * rect.h;
    const len = rect.w * (0.04 + rand() * 0.22);
    const ang = (rand() - 0.5) * 0.5 + (rand() > 0.5 ? 0 : Math.PI / 2);
    ctx.strokeStyle =
      rand() > 0.45
        ? `rgba(255,255,255,${spec.alpha})`
        : `rgba(0,0,0,${spec.alpha * 0.8})`;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.cos(ang) * len, y + Math.sin(ang) * len);
    ctx.stroke();
  }
  for (let i = 0; i < spec.dust; i += 1) {
    const r = rect.w * (0.0006 + rand() * 0.0022);
    ctx.fillStyle = `rgba(90,90,90,${spec.alpha * 1.4})`;
    ctx.beginPath();
    ctx.arc(rect.x + rand() * rect.w, rect.y + rand() * rect.h, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/**
 * Composites the full scene and returns the canvas.
 *
 * `cardNode` is the off-screen export copy of the card - plain, with no
 * watermark or blur veil - so nothing preview-only leaks into a download.
 */
export async function renderRealisticScene({
  cardNode,
  html2canvas,
  surfaceKey = "default",
  effectKey = "natural",
  tilt = -2,
  pixelBlur = false,
  damageLevel = "none",
  bgBlur = 40,
  cornerRadius,
  groundShadow = false,
  scale = 3,
}) {
  if (!cardNode) throw new Error("Card preview is not ready yet.");

  const surface = getSurface(surfaceKey);
  const vars = getEffect(effectKey).vars;

  if (document.fonts && document.fonts.ready) await document.fonts.ready;

  // The card raster: the same call the plain export makes, so text, photo and
  // QR come out exactly as they do today.
  const cardCanvas = await html2canvas(cardNode, {
    scale,
    backgroundColor: null,
    useCORS: true,
    logging: false,
    allowTaint: false,
  });

  const cardCssW = cardNode.offsetWidth || 856;
  const cardCssH = cardCssW / CARD_ASPECT;

  // Maps a pixel value written for the preview onto this export's pixels.
  const k = (cardCssW / PREVIEW_REF_W) * scale;

  const padX = (PAD_X_PX / PREVIEW_REF_W) * cardCssW;
  const padY = (PAD_Y_PX / PREVIEW_REF_W) * cardCssW;

  const outW = Math.round((cardCssW + padX * 2) * scale);
  const outH = Math.round((cardCssH + padY * 2) * scale);

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");

  // The backdrop is left square: a download is a photograph of a card on a
  // surface, so the corner roundness belongs to the card, not to the image.
  // Only the card's own clip below is rounded. The preview rounds this box
  // because it is a panel sitting in the page, which a file is not.
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, outW, outH);
  ctx.clip();

  // --- .rp-surface base + .rp-bg-photo ---------------------------------
  ctx.fillStyle = surface.image ? "#2a2a28" : "#3c3226";
  ctx.fillRect(0, 0, outW, outH);
  await drawBackdrop(ctx, surface, {
    w: outW,
    h: outH,
    scale,
    // --bg-blur in RealisticStage.jsx: (bgBlur / 100) * 3.2 css px.
    blurPx: (bgBlur / 100) * 3.2 * k,
    html2canvas,
  });

  // --- .rp-surface::before (desk noise, screen) ------------------------
  const grain2Opacity = num(vars, "--grain2-opacity", 0.13);
  const surfaceNoise = await loadImage(NOISE.surface.url);
  blendNoise(
    ctx,
    surfaceNoise,
    { x: 0, y: 0, w: outW, h: outH },
    "screen",
    grain2Opacity * 1.7,
    k
  );

  // --- .rp-surface::after (vignette) -----------------------------------
  const vignette = num(vars, "--vignette-opacity", 0.4);
  const vg = ctx.createRadialGradient(
    outW * 0.5,
    outH * 0.42,
    0,
    outW * 0.5,
    outH * 0.42,
    Math.max(outW * 0.85, outH * 0.78)
  );
  vg.addColorStop(0.45, "rgba(0,0,0,0)");
  vg.addColorStop(1, `rgba(0,0,0,${vignette})`);
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, outW, outH);

  // --- .rp-card-wrap (tilt + contact shadow) ---------------------------
  const cw = cardCssW * scale;
  const ch = cardCssH * scale;
  // The corner roundness set in Card settings, resolved for the export card's
  // width so the curve matches the preview in proportion.
  const cardRadius = cardRadiusPx(cardCssW, cornerRadius) * scale;
  const tint = surface.shadow || "20, 14, 8";

  ctx.save();
  ctx.translate(outW / 2, outH / 2);
  ctx.rotate((tilt * Math.PI) / 180);

  // .rp-card-wrap::before - the PVC edge peeking out below and behind.
  const edge = ctx.createLinearGradient(-cw / 2, -ch / 2, cw / 2, ch / 2);
  edge.addColorStop(0, "#e7e2d2");
  edge.addColorStop(0.55, "#bdb69e");
  edge.addColorStop(1, "#948d76");
  ctx.save();
  ctx.translate(1.5 * k, 2 * k);
  roundRectPath(ctx, -cw / 2, -ch / 2, cw, ch, cardRadius);
  ctx.fillStyle = edge;
  ctx.fill();
  ctx.restore();

  const cardFilter = [
    `sepia(${num(vars, "--card-sepia", 0.1)})`,
    "saturate(0.92)",
    `contrast(${num(vars, "--card-contrast", 0.97)})`,
    `brightness(${num(vars, "--card-brightness", 1)})`,
    pixelBlur ? `blur(${0.8 * k}px)` : "",
  ]
    .filter(Boolean)
    .join(" ");
  // Canvas2D carries one shadow per draw, but the CSS stacks two (three with
  // the ground shadow on). The card is opaque, so drawing it once per shadow
  // layers them: each pass lays down its own shadow and the final pass covers
  // the earlier copies exactly.
  const shadowPasses = [];
  if (groundShadow) {
    shadowPasses.push({ alpha: 0.32, blur: 26 * k, offsetY: 18 * k });
  }
  shadowPasses.push({ alpha: 0.34, blur: 10 * k, offsetY: 8 * k });
  shadowPasses.push({ alpha: 0.4, blur: 1 * k, offsetY: 1 * k });

  setFilter(ctx, cardFilter);
  shadowPasses.forEach((pass) => {
    ctx.shadowColor = `rgba(${tint}, ${pass.alpha})`;
    ctx.shadowBlur = pass.blur;
    ctx.shadowOffsetY = pass.offsetY;
    ctx.drawImage(cardCanvas, -cw / 2, -ch / 2, cw, ch);
  });
  clearFilter(ctx);
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  // Everything below is clipped to the card, the way `.ac-card-shield`'s
  // overflow:hidden clips the grain and sheen on screen.
  const cardRect = { x: -cw / 2, y: -ch / 2, w: cw, h: ch };
  ctx.save();
  roundRectPath(ctx, cardRect.x, cardRect.y, cw, ch, cardRadius);
  ctx.clip();

  // --- .rp-grain (three multiply layers) -------------------------------
  const grain1Opacity = num(vars, "--grain-opacity", 0.22);
  const [g1, g2, g3] = await Promise.all([
    loadImage(NOISE.grain1.url),
    loadImage(NOISE.grain2.url),
    loadImage(NOISE.grain3.url),
  ]);
  blendNoise(ctx, g1, cardRect, "multiply", grain1Opacity, k);
  blendNoise(ctx, g2, cardRect, "multiply", grain2Opacity, k);
  blendNoise(ctx, g3, cardRect, "multiply", grain2Opacity * 0.55, k);

  // --- damage overlay --------------------------------------------------
  drawDamage(ctx, damageLevel, cardRect, Math.round(cardCssW + tilt * 100));

  // --- .rp-sheen (soft-light glare) ------------------------------------
  const glare = num(vars, "--glare-opacity", 1);
  if (glare > 0) {
    ctx.save();
    ctx.globalCompositeOperation = "soft-light";
    ctx.globalAlpha = Math.min(glare, 1);

    // Hotspot. The preview jitters this per mount; the export takes the
    // documented default so re-downloading the same card is stable.
    const hx = cardRect.x + cw * 0.24;
    const hy = cardRect.y + ch * 0.18;
    const hot = ctx.createRadialGradient(
      hx,
      hy,
      0,
      hx,
      hy,
      Math.max(cw * 0.42, ch * 0.3)
    );
    hot.addColorStop(0, "rgba(255,255,255,0.5)");
    hot.addColorStop(0.7, "rgba(255,255,255,0)");
    ctx.fillStyle = hot;
    ctx.fillRect(cardRect.x, cardRect.y, cw, ch);

    const sweep = ctx.createLinearGradient(
      cardRect.x,
      cardRect.y + ch,
      cardRect.x + cw,
      cardRect.y
    );
    sweep.addColorStop(0, "rgba(255,255,255,0.18)");
    sweep.addColorStop(0.26, "rgba(255,255,255,0)");
    sweep.addColorStop(0.74, "rgba(255,255,255,0)");
    sweep.addColorStop(1, "rgba(255,255,255,0.08)");
    ctx.fillStyle = sweep;
    ctx.fillRect(cardRect.x, cardRect.y, cw, ch);
    ctx.restore();
  }

  ctx.restore(); // card clip
  ctx.restore(); // tilt
  ctx.restore(); // surface clip

  return canvas;
}
